import { useEffect, useState } from 'react'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'

import type { SellerCollectsOrderIds } from '@/buyer-portal/queries/sellerCollectsOrderIdsQuery'

/**
 * Session-scoped, like the banner's own dismissal, and per buyer: one tab can
 * sign out and sign back in as somebody else, and an order history is about a
 * person.
 */
const storageKey = (accountId: string) =>
  `bstock.sellerCollectsOrderIds.${accountId}`

const isOrderIds = (value: unknown): value is SellerCollectsOrderIds =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(
    (ids) =>
      Array.isArray(ids) && ids.every((id) => typeof id === 'string' && id)
  )

/**
 * Every order id the sources name, per seller, without duplicates.
 *
 * The scan and the session cache each know something the other does not: the
 * scan is authoritative but never refetched, the cache carries the orders
 * folded in since it ran. Neither alone answers "has this buyer ordered from
 * this seller" for a whole session.
 */
export const mergeOrderIds = (
  ...sources: Array<SellerCollectsOrderIds | null | undefined>
): SellerCollectsOrderIds => {
  const merged: SellerCollectsOrderIds = {}

  for (const source of sources) {
    for (const [seller, ids] of Object.entries(source ?? {})) {
      merged[seller] = [...new Set([...(merged[seller] ?? []), ...ids])]
    }
  }

  return merged
}

export const readCachedOrderIds = (
  accountId: string
): SellerCollectsOrderIds | null => {
  try {
    const stored = sessionStorage.getItem(storageKey(accountId))
    if (stored === null) return null
    const parsed: unknown = JSON.parse(stored)
    return isOrderIds(parsed) ? parsed : null
  } catch {
    // Private browsing or blocked storage — fall back to asking the API.
    return null
  }
}

export const writeCachedOrderIds = (
  accountId: string,
  orderIds: SellerCollectsOrderIds
): void => {
  try {
    sessionStorage.setItem(storageKey(accountId), JSON.stringify(orderIds))
  } catch {
    // Then the lookup costs one request per page view, as it did before.
  }
}

/**
 * This session's stored order ids, and the buyer they belong to.
 *
 * Both hooks that read the cache need the same three rules, and a drift
 * between two copies of them would misbehave silently rather than fail:
 * `undefined` means storage has not been read yet, `null` means it held
 * nothing, and a disabled caller does not touch storage at all — it mounts on
 * surfaces that render once per card.
 *
 * Read after mount rather than in render: sessionStorage does not exist while
 * server rendering, and a value read there would not survive hydration.
 */
export const useCachedSellerCollectsOrderIds = ({
  enabled = true,
  orderId,
}: {
  enabled?: boolean
  /**
   * Re-reads when the order in view changes. A layout reused for the next
   * order would otherwise answer from the snapshot it took before the
   * previous one was folded in.
   */
  orderId?: string
} = {}): {
  accountId: string | undefined
  cachedOrderIds: SellerCollectsOrderIds | null | undefined
} => {
  const accountId = useDecodedAccessToken()?.accountId
  const [cachedOrderIds, setCachedOrderIds] = useState<
    SellerCollectsOrderIds | null | undefined
  >(undefined)

  useEffect(() => {
    if (!enabled) return

    // oxlint-disable-next-line react/set-state-in-effect
    setCachedOrderIds(
      accountId === undefined ? null : readCachedOrderIds(accountId)
    )
  }, [accountId, enabled, orderId])

  return { accountId, cachedOrderIds }
}
